import React, { useState, useCallback } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Input from "aldoo-ra/Input"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Badge from "aldoo-ra/Badge"
import Avatar from "aldoo-ra/Avatar"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { api } from "aldoo-ra/API"
import { url } from "aldoo-ra/CMS/url"
import { UserPlatformAPI } from "../user-api"
import { PermissionsPlatformAPI } from "../permissions-api"
import PermissionsEditor from "./PermissionsEditor"
import {
  UserIcon,
  PlusIcon,
  MinusIcon,
  KeyIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  TrashIcon,
  CheckIcon
} from "@heroicons/react/24/outline"

// CRUD API
const crudAPI = api("crud")

// CRUD Functions for Permissions model
const GetUserPermissions = async (options = {}) => {
  let queryParams = {
    model: "Permission",
    page: options.page || 1,
    itemsPerPage: options.itemsPerPage || 20,
    populate: "user",
    sort: { updatedAt: -1 }
  }

  // Handle search - DataTable passes search as an object with $or conditions
  if (options.search && typeof options.search === 'object' && options.search.$or) {
    // Convert DataTable search format to proper match format
    queryParams.match = {
      $or: options.search.$or.map(condition => {
        // Convert each condition to proper regex format
        const field = Object.keys(condition)[0]
        const value = condition[field]
        return {
          [field]: { $regex: value, $options: "i" }
        }
      })
    }
  }
  
  const response = await crudAPI({
    get: queryParams
  })
  
  return response
}



const DeleteUserPermission = async (permissionId) => {
  try {
    const response = await crudAPI({
      delete: {
        model: "Permission",
        match: { _id: permissionId },
        mode: "hard"
      }
    })
    
    return response
  } catch (error) {
    console.error("Error hard deleting permission:", error)
    return { result: false, error: error.message }
  }
}


/**
 * User Permissions Tab
 * Manages permissions assigned to individual users using the Permissions model
 */
const UserPermissionsTab = ({ refreshKey, onRefresh, onError, loading, setLoading }) => {
  // UI State
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [selectedPermissionRecord, setSelectedPermissionRecord] = useState(null)
  
  // User Filter State (matching store transactions pattern)
  const [userFilter, setUserFilter] = useState(null)
  
  // Permission State
  const [availablePermissions, setAvailablePermissions] = useState([])
  const [currentUserPermissions, setCurrentUserPermissions] = useState([])
  
  // Search State for Available Permissions
  const [permissionSearchTerm, setPermissionSearchTerm] = useState("")
  
  // Search State for Current User Permissions
  const [currentPermissionSearchTerm, setCurrentPermissionSearchTerm] = useState("")
  
  // Form State
  const [formError, setFormError] = useState("")

  // Load available permissions
  React.useEffect(() => {
    const loadPermissions = async () => {
      try {
        const permissions = await PermissionsPlatformAPI.GetAvailablePermissions()
        setAvailablePermissions(permissions || [])
      } catch (error) {
        console.error("Failed to load permissions:", error)
      }
    }
    loadPermissions()
  }, [refreshKey])

  // Get selected user from filter
  const getSelectedUser = () => {
    if (!userFilter || !userFilter.length) return null
    return userFilter[0]
  }

  // Check if selected user already has permissions
  const [existingPermissions, setExistingPermissions] = useState([])
  const [permissionsLoading, setPermissionsLoading] = useState(false)

  // Check if user already exists in permissions list
  const checkUserExists = async (userId) => {
    if (!userId) return false
    
    try {
      const response = await crudAPI({
        get: {
          model: "Permission",
          match: { user: userId },
          populate: "user"
        }
      })
      
      // Handle both array response and object with data property
      let exists = false
      if (Array.isArray(response) && response.length > 0) {
        exists = true
      } else if (response.data && response.data.length > 0) {
        exists = true
      }
      
      return exists
    } catch (error) {
      return false
    }
  }

  // Monitor selected user changes
  React.useEffect(() => {
    const selectedUser = getSelectedUser()
    
    if (selectedUser) {
      setPermissionsLoading(true)
      checkUserExists(selectedUser._id).then(exists => {
        setExistingPermissions(exists ? [selectedUser] : [])
        setPermissionsLoading(false)
      })
    } else {
      setExistingPermissions([])
      setPermissionsLoading(false)
    }
  }, [userFilter])

  // Handle editing user permissions
  const handleEditPermissions = useCallback(async (permissionRecord) => {
    setSelectedPermissionRecord(permissionRecord)
    setCurrentUserPermissions([...permissionRecord.permissions])
    setPermissionSearchTerm("") // Reset search when opening dialog
    setCurrentPermissionSearchTerm("") // Reset current permissions search when opening dialog
    setFormError("")
    setShowEditDialog(true)
  }, [])

  // Handle adding/removing permission to/from user (toggle behavior)
  const handleAddPermission = useCallback((permission) => {
    if (currentUserPermissions.includes(permission.permission)) {
      // Remove permission if it's already assigned
      setCurrentUserPermissions(prev => prev.filter(p => p !== permission.permission))
      showNotification({
        message: `Removed permission: ${permission.action} on ${permission.resource}`,
        className: "bg-admin_warning text-white"
      })
    } else {
      // Add permission if it's not assigned
      setCurrentUserPermissions(prev => [...prev, permission.permission])
      showNotification({
        message: `Added permission: ${permission.action} on ${permission.resource}`,
        className: "bg-admin_success text-white"
      })
    }
  }, [currentUserPermissions])

  // Handle removing permission from user
  const handleRemovePermission = useCallback((permission) => {
    const [resource, action] = permission.split(':')
    setCurrentUserPermissions(prev => prev.filter(p => p !== permission))
    showNotification({
      message: `Removed permission: ${action} on ${resource}`,
      className: "bg-admin_warning text-white"
    })
  }, [])

  // Handle adding all permissions from a resource group
  const handleAddAllResourcePermissions = useCallback((resource) => {
    const resourcePermissions = availablePermissions
      .filter(p => p.resource === resource)
      .map(p => p.permission)
    
    const newPermissions = [...new Set([...currentUserPermissions, ...resourcePermissions])]
    const addedCount = newPermissions.length - currentUserPermissions.length
    
    if (addedCount > 0) {
      setCurrentUserPermissions(newPermissions)
      showNotification({
        message: `Added ${addedCount} permission${addedCount > 1 ? 's' : ''} for ${resource}`,
        className: "bg-admin_success text-white"
      })
    }
  }, [availablePermissions, currentUserPermissions])

  // Handle removing all permissions from a resource group
  const handleRemoveAllResourcePermissions = useCallback((resource) => {
    const newPermissions = currentUserPermissions.filter(permission => 
      !permission.startsWith(`${resource}:`)
    )
    const removedCount = currentUserPermissions.length - newPermissions.length
    
    if (removedCount > 0) {
      setCurrentUserPermissions(newPermissions)
      showNotification({
        message: `Removed ${removedCount} permission${removedCount > 1 ? 's' : ''} for ${resource}`,
        className: "bg-admin_warning text-white"
      })
    }
  }, [currentUserPermissions])

  // Handle updating user permissions
  const handleUpdatePermissions = useCallback(async () => {
    if (!selectedPermissionRecord) return

    setLoading(true)
    setFormError("")

    try {
      const result = await UpdateUserPermission(
        selectedPermissionRecord._id,
        currentUserPermissions
      )

      // Handle empty object response as success
      if (result && (result.result === true || Object.keys(result).length === 0)) {
        setShowEditDialog(false)
        setSelectedPermissionRecord(null)
        setCurrentUserPermissions([])
        onRefresh()
        
        showNotification({
          message: `Permissions updated for ${selectedPermissionRecord.user.email}`,
          className: "bg-admin_success text-white"
        })
      } else {
        const errorMessage = result?.error || result?.message || "Failed to update permissions"
        setFormError(errorMessage)
        console.error("Update failed with result:", result)
      }
    } catch (error) {
      setFormError("Failed to update permissions")
      console.error("Error updating permissions:", error)
    } finally {
      setLoading(false)
    }
  }, [selectedPermissionRecord, currentUserPermissions, setLoading, onRefresh])

  // Handle deleting user permission record
  const handleDeletePermissionRecord = useCallback(async (permissionRecord) => {
    showPromptDialog({
      title: "Delete Permission Record",
      message: `Are you sure you want to permanently delete all permissions for this user? This action cannot be undone.`,
      details: `User: ${permissionRecord.user?.name || permissionRecord.user?.email || 'Unknown User'}`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        setLoading(true)
        try {
          const result = await DeleteUserPermission(permissionRecord._id)
          
          if (result.result) {
            onRefresh()
            showNotification({
              message: `Permission record deleted for ${permissionRecord.user?.name || permissionRecord.user?.email}`,
              className: "bg-admin_success text-white"
            })
          } else {
            const errorMessage = result.error || result.message || "Failed to delete permission record"
            showNotification({
              message: errorMessage,
              className: "bg-admin_error text-white"
            })
          }
        } catch (error) {
          console.error("Error deleting permission record:", error)
          showNotification({
            message: "Failed to delete permission record",
            className: "bg-admin_error text-white"
          })
        } finally {
          setLoading(false)
        }
      },
      onCancel: () => {
        // Do nothing
      }
    })
  }, [setLoading, onRefresh])

  // Handle creating new user permission record
  const handleCreateUserPermissions = useCallback(async () => {
    const selectedUser = getSelectedUser()
    if (!selectedUser) {
      setFormError("Please select a user first")
      return
    }

    setLoading(true)
    setFormError("")

    try {
      const result = await PermissionsPlatformAPI.CreateUserPermission(selectedUser._id, [])

      if (result.result) {
        onRefresh()
        setUserFilter(null)
        
        showNotification({
          message: `Permission record created for ${selectedUser.email}`,
          className: "bg-admin_success text-white"
        })
      } else {
        setFormError(result.error || "Failed to create permission record")
      }
    } catch (error) {
      setFormError("Failed to create permission record")
      console.error("Error creating permission record:", error)
    } finally {
      setLoading(false)
    }
  }, [userFilter, setLoading, onRefresh])

  // Filter and group available permissions by resource
  const filteredAndGroupedPermissions = React.useMemo(() => {
    // First filter permissions based on search term
    const filteredPermissions = availablePermissions.filter(perm => {
      if (!permissionSearchTerm.trim()) return true
      
      const searchLower = permissionSearchTerm.toLowerCase()
      return (
        perm.resource.toLowerCase().includes(searchLower) ||
        perm.action.toLowerCase().includes(searchLower) ||
        perm.description.toLowerCase().includes(searchLower) ||
        perm.permission.toLowerCase().includes(searchLower)
      )
    })
    
    // Then group filtered permissions by resource
    const groups = {}
    filteredPermissions.forEach(perm => {
      if (!groups[perm.resource]) {
        groups[perm.resource] = {
          permissions: [],
          resourceDescription: perm.resourceDescription
        }
      }
      groups[perm.resource].permissions.push(perm)
    })
    return groups
  }, [availablePermissions, permissionSearchTerm])

  // Filter current user permissions based on search term and group by resource
  const filteredCurrentUserPermissions = React.useMemo(() => {
    let permissions = currentUserPermissions
    
    if (currentPermissionSearchTerm.trim()) {
      const searchLower = currentPermissionSearchTerm.toLowerCase()
      permissions = permissions.filter(permission => 
        permission.toLowerCase().includes(searchLower)
      )
    }
    
    // Group permissions by resource
    const groups = {}
    permissions.forEach(permission => {
      const [resource, action] = permission.split(':')
      if (!groups[resource]) {
        groups[resource] = {
          permissions: [],
          resourceDescription: availablePermissions.find(p => p.resource === resource)?.resourceDescription || `${resource} management`
        }
      }
      groups[resource].permissions.push({
        permission,
        resource,
        action,
        description: availablePermissions.find(p => p.permission === permission)?.description || `${action} on ${resource}`
      })
    })
    
    return groups
  }, [currentUserPermissions, currentPermissionSearchTerm, availablePermissions])

  // DataTable renders (ParamIO style)
  const renders = [
    {
      id: "user",
      title: "User",
      headerClassName: "w-[60%]",
      render: (_, permissionRecord) => (
        <div className="flex items-center space-x-3 overflow-hidden">
          <Avatar user={permissionRecord.user} variant="admin_medium" />

          <div>
            <Typography 
              variant="admin_txt1" 
              className="block font-medium"
            >
              {permissionRecord.user?.name || 'Unknown User'}
            </Typography>

            {permissionRecord.user?.email && (
              <Typography variant="admin_txt2_dim">
                {permissionRecord.user.email}
              </Typography>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "permissions_count",
      title: "Permissions",
      headerClassName: "w-[140px]",
      render: (_, permissionRecord) => {
        const permissionCount = permissionRecord.permissions?.length || 0
        
        return (
          <div>
            <Typography 
              variant="admin_txt1" 
              className="block font-medium cursor-pointer hover:text-admin_primary transition-colors"
              onClick={() => handleEditPermissions(permissionRecord)}
              title="Click to edit permissions"
            >
              {permissionCount} permissions
            </Typography>

            <Typography variant="admin_txt2_dim">
              Last updated: {new Date(permissionRecord.updatedAt).toLocaleDateString()}
            </Typography>
          </div>
        )
      },
    },
    {
      id: "actions",
      title: "Actions",
      headerClassName: "w-[80px] text-center",
      render: (_, permissionRecord) => (
        <div className="flex items-center justify-center space-x-1">
          <Button
            variant="admin_icon"
            onClick={() => handleEditPermissions(permissionRecord)}
            className="text-admin_neutral_3 px-2"
            title="Edit permissions"
          >
            <PencilIcon className="w-4 h-4 " />
          </Button>
          
          <Button
            variant="admin_icon"
            onClick={() => handleDeletePermissionRecord(permissionRecord)}
            className="text-admin_neutral_3 hover:text-admin_error px-2"
            title="Delete permission record"
          >
            <TrashIcon className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div>
      {/* User Search Section */}
      <div className="bg-admin_canvas rounded-lg p-4">
        <div className="flex xl:flex-col 2xl:flex-row items-center xl:items-start 2xl:items-center gap-3">
          <div className="flex-1">
            <DropdownSelect
              variant="admin"
              searchEnabled
              useExternalSearch
              enableResetButton
              showCheckbox={false}
              apiMethod={async (params) => {
                if (!params.searchTerm || params.searchTerm.length < 2) {
                  return { data: [] }
                }

                const response = await UserPlatformAPI.SearchUsers(params.searchTerm)
                return response
              }}
              onDataLoaded={(rawData) => {
                return rawData.map(user => ({
                  _id: user._id,
                  name: user.name || 'Unknown User',
                  email: user.email || 'No email',
                  value: user._id,
                  label: `${user.name || 'Unknown User'} (${user.email || 'No email'})`,
                  ...user
                }))
              }}
              searchField="name email"
              valueField="_id"
              nameField="label"
              fixedWidth="400px"
              placeholder="Search and select user..."
              noDataMessage="Type to search users"
              value={userFilter}
              onChange={(value) => {
                setUserFilter(value)
                setFormError("")
              }}
              onValueItemRender={(user) => {
                if (!user) return "Search and select user..."
                return (
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-admin_primary flex items-center justify-center flex-shrink-0">
                      {user.avatar ? (
                        <img src={url(user.avatar)} alt={user.name} className="w-6 h-6 rounded-full object-cover" />
                      ) : (
                        <span className="text-white font-medium text-xs">
                          {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-admin_text truncate">
                        {user.name || 'Unknown User'}
                      </div>
                      <div className="text-xs text-admin_text_secondary truncate">
                        {user.email || 'No email'}
                      </div>
                    </div>
                  </div>
                )
              }}
              onItemRender={(user) => (
                <div className="flex items-center gap-3 p-2">
                  <Avatar user={user} variant="admin_dropdown" textVariant="admin_label" />
                  
                  <div className="flex-1">
                    <Typography 
                      variant="admin_label"
                      className="leading-none my-0"
                    >
                      {user.name || 'Unknown User'}
                    </Typography>

                    <Typography 
                      variant="admin_txt2_dim"
                      className="leading-none"
                    >
                      {user.email || 'No email'}
                    </Typography>
                  </div>
                </div>
              )}
            />
          </div>

          {/* Create Permissions Button */}
          {getSelectedUser() && (
            <div className="flex-shrink-0">
              {existingPermissions.length > 0 ? (
                <div className="w-fit flex items-center gap-1 px-4 py-2 bg-admin_paper text-admin_success rounded-lg">
                  <CheckIcon className="w-4 h-4" />

                  <span className="text-sm">
                    Already in permissions list
                  </span>
                </div>
              ) : (
                <Button
                  variant="admin_primary"
                  onClick={handleCreateUserPermissions}
                  disabled={loading || permissionsLoading}
                  className="gap-2"
                >
                  <PlusIcon className="w-4 h-4" />
                  {permissionsLoading ? "Checking..." : "Create Permissions"}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Search Error */}
        {formError && (
          <div className="mt-3 bg-admin_error/10 border border-admin_error/20 rounded-lg p-3 flex items-start gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-admin_error flex-shrink-0 mt-0.5" />

            <Typography 
              variant="admin_txt2" 
              className="text-admin_error"
            >
              {formError}
            </Typography>
          </div>
        )}
      </div>

      {/* Permissions Table */}
      <DataTable
        variant="admin_browser"
        apiMethod={GetUserPermissions}
        apiDataField="data"
        localUserPreferencesKey="admin.platformAccess.userPermissions"
        renders={renders}
        refreshKey={refreshKey}
        preferredHeight="70vh"
        itemHeight="h-[70px]"
        itemsPerPage={15}
        showSearch={true}
        searchField="user.email user.name permissions"
        searchPlaceholder="Search user permissions..."
        styleOverrides={{
          container: "relative w-full flex flex-col h-[90vh] justify-start bg-admin_paper rounded-3xl py-6 pb-4",
          table: "w-full table-fixed",
          header: "w-full flex justify-between items-end gap-2 mt-4 mb-8",
          searchInput: "absolute top-2 right-0 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
        }}
        noData={
          <div className="text-center py-8">
            <UserGroupIcon className="w-12 h-12 text-admin_text_secondary mx-auto mb-4" />

            <Typography 
              variant="admin_body_dim" 
              className="mb-1"
            >
              No user permissions found
            </Typography>

            <Typography variant="admin_txt2_dim">
              Search for users above to create permission records
            </Typography>
          </div>
        }
      />

      {/* Edit Permissions Dialog */}
      <PermissionsEditor
        isOpen={showEditDialog}
        onClose={() => {
          setShowEditDialog(false)
          setSelectedPermissionRecord(null)
        }}
        user={selectedPermissionRecord?.user}
        onPermissionChange={(updatedPermissions) => {
          // Refresh the data table to show updated permissions
          onRefresh()
        }}
        loading={loading}
        refreshKey={refreshKey}
      />
    </div>
  )
}

export default UserPermissionsTab